import { Component } from "react"

/* Components */
import { InnerLayout, Pagination } from "../../components"

/* Widgets */
import Filter from "./Filter"

/* REST API */
import { rounds } from "../../api/Player"

/* Helpers */
import { utils } from "../../helpers"

/* Icons */
import { CloseIcon, FilterIcon, RoundIcon, TableIcon, StudioIcon, OperatorIcon, ArrowIcon, AlertIcon, RefundIcon, SessionIcon, CopyIcon, LayersIcon, DebitIcon, CreditIcon, NetIcon } from "./PlayerIcons"

/* Utils */
import { getCurrency, getState, getStateText, isEmpty, formatCount, formatSid, formatDay, formatTime } from "./PlayerUtils"

/* Sweet alert */
import Swal from "../../helpers/alert"

/* Translate */
import { t } from "../../translates/translate"

/* Redux */
import { connect } from "react-redux"

/* Moment */
import moment from "moment"
import "moment/locale/ru"


/* PAGE SIZE — THE SAME NUMBER THE CONTROLLER USES */
const SIZE = 10

/* EMPTY ANSWER — THE SAME SHAPE THE CONTROLLER SENDS */
const EMPTY = { rounds: 0, transactions: 0, games: 0, debit: 0, credit: 0, ggr: 0 }


/* Widget Rounds */
class Rounds extends Component {

    constructor(props) {
        super(props)

        this.state = {
            status: "loading",
            data: [],
            count: 0,
            page: 1,
            opened: [],
            totals: EMPTY,

            /* Filter — a game chosen on the card of the player comes as a ready filter */
            selected: "all",
            start: null,
            end: null,
            tables: props.table ? [props.table] : [],
        }

        this._filterModal = null
    }

    componentDidMount = () => {
        this.load()
    }

    /* Load Action */
    load = (reload = false, forcePage = null) => {

        /* Fields */
        const { player, sid } = this.props
        const { page: current, tables, start, end } = this.state
        const page = forcePage !== null ? forcePage : current

        /* Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* Send to REST API */
        rounds({
            player,
            page,
            sid: sid ? sid : "",
            tables: tables.map((item) => item.id).join(","),
            start: start ? moment(start).format("YYYY-MM-DD HH:mm:ss") : "",
            end: end ? moment(end).format("YYYY-MM-DD HH:mm:ss") : "",
        }).then((response) => {
            if (response.status === 200) {

                /* Fields */
                const list = response.data.list

                /* The first round is opened by default — the modal must not look empty */
                const opened = list.length > 0 ? [list[0].round] : []

                this.setState({
                    status: "ok",
                    data: list,
                    count: response.data.count,
                    totals: response.data.totals ? response.data.totals : EMPTY,
                    page,
                    opened,
                })
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission", data: [], count: 0, totals: EMPTY, opened: [] })
            }
            else if (response.status === 498) {
                this.setState({ status: "key", data: [], count: 0, totals: EMPTY, opened: [] })
            }
            else {
                this.setState({ status: "error", data: [], count: 0, totals: EMPTY, opened: [] })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [], count: 0, totals: EMPTY, opened: [] })
        })

    }

    /* Change Page */
    changePage = (page) => {

        /* Fields */
        const { scrollToTop } = this.props

        this.setState({ page }, () => {
            this.load(true)
            if (scrollToTop) {
                scrollToTop()
            }
        })
    }

    /* Toggle A Round */
    toggle = (round) => {

        /* Fields */
        const { opened } = this.state

        /* Get Index */
        const index = opened.indexOf(round)

        /* Check Index */
        if (index > -1) {
            opened.splice(index, 1)
        }
        else {
            opened.push(round)
        }

        this.setState({ opened })
    }

    /* Check A Round */
    checkRound = (round) => this.state.opened.indexOf(round) > -1

    /* Apply Filter — every filter restarts from the first page */
    applyFilter = ({ selected, start, end, tables }) => {
        this.setState({ selected, start, end, tables, page: 1 }, () => this.load(true))
    }

    /* Remove One Game Of The Filter */
    removeTable = (table) => {

        /* Fields */
        const { tables } = this.state

        /* Get Index */
        const index = tables.findIndex((item) => parseInt(item.id) === parseInt(table.id))

        /* Check Index */
        if (index > -1) {
            tables.splice(index, 1)
        }

        this.setState({ tables, page: 1 }, () => this.load(true))
    }

    /* Copy A Value */
    copy = (value, message) => {

        /* Check Value */
        if (!value || !navigator.clipboard) {
            Swal.fire(utils.notification(t("gtx.copyFailed"), "error"))
            return
        }

        navigator.clipboard.writeText(String(value)).then(() => {
            Swal.fire(utils.notification(message, "success"))
        }).catch(() => {
            Swal.fire(utils.notification(t("gtx.copyFailed"), "error"))
        })

    }

    /* Copy The Number Of A Round — the click must not fold the accordion */
    copyRound = (event, round) => {

        event.stopPropagation()

        this.copy(round, t("plr.roundCopied"))
    }

    /* Count Of The Active Filters */
    getCount = () => {

        /* Fields */
        const { selected, tables } = this.state
        let count = 0

        if (selected !== "all") {
            count = count + 1
        }

        if (tables.length > 0) {
            count = count + 1
        }

        /* Send Result */
        return count
    }

    /* Draw The Toolbar */
    _toolbar = () => {

        /* Fields */
        const { count, selected, start, end, tables } = this.state
        const active = this.getCount()

        return (
            <div className="plr--toolbar">

                <div className="plr--toolbar-title">
                    <RoundIcon size={14} />
                    {t("player.roundsTotal")}
                    <span className="plr--counter">{formatCount(count)}</span>
                </div>

                <div className="plr--toolbar-side">

                    <div
                        onClick={() => this._filterModal.open({ selected, start, end, tables })}
                        className={`plr--filter light ${active > 0 ? "active" : ""}`}
                    >
                        <FilterIcon size={14} />
                        <span>{t("gtx.filter")}</span>
                        {active > 0 && <div className="plr--filter-count">{active}</div>}
                    </div>

                </div>

            </div>
        )
    }

    /* Draw One Number Of The Filter */
    _stat = (icon, title, value, tone = "") => {

        return (
            <div className={`plr--stat ${tone}`}>

                <div className="plr--stat-icon">{icon}</div>

                <div className="plr--stat-text">
                    <div className="plr--stat-title">{title}</div>
                    <div className="plr--stat-value small">{value}</div>
                </div>

            </div>
        )
    }

    /* Draw The Numbers — what the current filter really holds, the money is counted in USD */
    _numbers = () => {

        /* Fields */
        const { status, totals } = this.state

        /* Check Status */
        if (status !== "ok") {
            return null
        }

        /* Fields */
        const ggr = Number(totals.ggr) || 0

        return (
            <div className="plr--stats inner compact single">

                {this._stat(<RoundIcon />, t("plr.roundsShort"), formatCount(totals.rounds), "blue")}
                {this._stat(<LayersIcon />, t("player.roundTxs"), formatCount(totals.transactions), "cyan")}
                {this._stat(<TableIcon />, t("plr.games"), formatCount(totals.games), "violet")}
                {this._stat(<DebitIcon />, "Debit", utils.convertor(totals.debit, "USD", 1), "red")}
                {this._stat(<CreditIcon />, "Credit", utils.convertor(totals.credit, "USD", 1), "green")}
                {this._stat(<NetIcon />, "GGR", utils.convertor(ggr, "USD", 1), ggr >= 0 ? "gold" : "red")}

            </div>
        )
    }

    /* Draw The Chips Of The Filter — they take a line of their own, a long name of a game needs the room */
    _chips = () => {

        /* Fields */
        const { tables } = this.state
        const { sid, clearSession } = this.props

        /* Check Chips */
        if (!sid && tables.length === 0) {
            return null
        }

        return (
            <div className="plr--filters-row">

                {/* Session — the rounds are narrowed down to one entrance of the player */}
                {sid &&
                    <div className="plr--session-chip">
                        <SessionIcon size={12} />
                        <span title={sid}>{formatSid(sid)}</span>
                        <div onClick={() => clearSession && clearSession()} className="plr--session-chip-clear" title={t("plr.clearSession")}>
                            <CloseIcon size={11} />
                        </div>
                    </div>
                }

                {/* Games — a game chosen on the card of the player, or picked inside the filter */}
                {tables.map((item, index) =>
                    <div className="plr--session-chip plr--chip-game" key={`${index}`}>
                        <TableIcon size={12} />
                        <span title={utils.checkValue(item.name, "")}>{utils.checkValue(item.name, item.slug)}</span>
                        <div onClick={() => this.removeTable(item)} className="plr--session-chip-clear" title={t("plr.clearGame")}>
                            <CloseIcon size={11} />
                        </div>
                    </div>
                )}

            </div>
        )
    }

    /* Draw The Head Of A Round */
    _head = (item) => {

        /* Fields */
        const currency = getCurrency(item.currency)
        const debit = parseFloat(item.debit) || 0
        const credit = parseFloat(item.credit) || 0
        const ggr = debit - credit
        const usd = (parseFloat(item.debitUSD) || 0) - (parseFloat(item.creditUSD) || 0)

        return (
            <div onClick={() => this.toggle(item.round)} className="plr--round-head">

                {/* Round */}
                <div className="plr--round-main">

                    <div className="plr--round-icon">
                        <RoundIcon size={16} />
                    </div>

                    <div className="plr--round-text">

                        <div className="plr--round-number">
                            <span>{utils.checkValue(item.round, "—")}</span>
                            <div
                                onClick={(event) => this.copyRound(event, item.round)}
                                className="plr--round-copy"
                                title={t("plr.copyRound")}
                            >
                                <CopyIcon size={12} />
                            </div>
                        </div>

                        <div className="plr--round-note">
                            <TableIcon size={11} />
                            <span>{utils.checkValue(item.game, item.slug)}</span>
                            {/* Studio — the short code of the studio is read faster than its full name */}
                            {item.studioSlug && <><StudioIcon size={11} /><span className="plr--capital" title={utils.checkValue(item.studio, "")}>{item.studioSlug}</span></>}
                            {item.operator && <><OperatorIcon size={11} /><span>{item.operator}</span></>}
                        </div>

                    </div>

                </div>

                {/* Transactions */}
                <div className="plr--round-cell">
                    <div className="plr--round-label">{t("player.roundTxs")}</div>
                    <div className="plr--round-value">
                        {formatCount(item.count)}
                        {parseInt(item.failed) > 0 && <span className="plr--pill red"><AlertIcon size={10} />{item.failed}</span>}
                        {parseInt(item.refunds) > 0 && <span className="plr--pill orange"><RefundIcon size={10} />{item.refunds}</span>}
                    </div>
                </div>

                {/* Debit */}
                <div className="plr--round-cell">
                    <div className="plr--round-label">Debit</div>
                    <div className="plr--round-value">{utils.convertor(debit, currency, 1)}</div>
                </div>

                {/* Credit */}
                <div className="plr--round-cell">
                    <div className="plr--round-label">Credit</div>
                    <div className="plr--round-value">{utils.convertor(credit, currency, 1)}</div>
                </div>

                {/* GGR */}
                <div className="plr--round-cell">
                    <div className="plr--round-label">GGR</div>
                    <div className={`plr--round-value ${ggr > 0 ? "green" : ""} ${ggr < 0 ? "red" : ""}`}>
                        {ggr > 0 && "+ "}
                        {ggr < 0 && "- "}
                        {utils.convertor(Math.abs(ggr), currency, 1)}
                    </div>
                    <div className="plr--round-sub">{utils.convertor(usd, "USD", 1)}</div>
                </div>

                {/* Date */}
                <div className="plr--round-cell">
                    <div className="plr--round-label">{t("gtx.date")}</div>
                    <div className="plr--round-value">{formatTime(item.startedAt)}</div>
                    <div className="plr--round-sub">{formatDay(item.startedAt)}</div>
                </div>

                {/* Arrow */}
                <div className="plr--round-arrow">
                    <ArrowIcon size={15} />
                </div>

            </div>
        )
    }

    /* Draw The Transactions Of A Round */
    _transactions = (item) => {

        /* Fields */
        const list = item.transactions ? item.transactions : []

        /* Check The Accordion */
        if (!this.checkRound(item.round)) {
            return null
        }

        /* Empty */
        if (list.length === 0) {
            return (
                <div className="plr--round-body">
                    <div className="plr--chips-empty">{t("plr.txEmpty")}</div>
                </div>
            )
        }

        return (
            <div className="plr--round-body">

                {/* Head */}
                <div className="plr--tx-header">
                    <div className="plr--tx-cell plr--tx-index">#</div>
                    <div className="plr--tx-cell plr--tx-reason">{t("gtx.reason")}</div>
                    <div className="plr--tx-cell plr--tx-state">{t("gtx.status")}</div>
                    <div className="plr--tx-cell plr--tx-type">{t("gtx.type")}</div>
                    <div className="plr--tx-cell plr--tx-total">{t("gtx.amount")}</div>
                    <div className="plr--tx-cell plr--tx-balance">{t("gtx.balance")}</div>
                    <div className="plr--tx-cell plr--tx-date">{t("gtx.date")}</div>
                </div>

                {/* Body */}
                {list.map((transaction, index) => {

                    /* Fields */
                    const currency = getCurrency(transaction.currency)

                    return (
                        <div className="plr--tx-row" key={`${index}`}>

                            {/* Index */}
                            <div className="plr--tx-cell plr--tx-index">{index + 1}</div>

                            {/* Reason */}
                            <div className="plr--tx-cell plr--tx-reason">
                                <div className="plr--tx-name">
                                    {utils.checkValue(transaction.reason, "—")}
                                    {parseInt(transaction.box) > 0 && <span className="plr--box">BOX {transaction.box}</span>}
                                    {parseInt(transaction.isRefund) === 1 && <span className="plr--box refund">{t("gtx.isRefund")}</span>}
                                </div>
                                <div className="plr--tx-note">{utils.checkValue(transaction.number, "—")}</div>
                            </div>

                            {/* Status */}
                            <div className="plr--tx-cell plr--tx-state">
                                <div className={`plr--state ${getState(transaction.status)}`}>{getStateText(transaction.status)}</div>
                            </div>

                            {/* Type */}
                            <div className="plr--tx-cell plr--tx-type">
                                <span className={`plr--type ${transaction.type}`}>{transaction.type}</span>
                            </div>

                            {/* Total */}
                            <div className="plr--tx-cell plr--tx-total">
                                <div className={`plr--tx-value ${transaction.type}`}>{utils.convertor(transaction.total, currency, 1)}</div>
                                <div className="plr--tx-note">{utils.convertor(transaction.totalUSD, "USD", 1)}</div>
                            </div>

                            {/* Balance */}
                            <div className="plr--tx-cell plr--tx-balance">
                                {isEmpty(transaction.balanceBefore) && isEmpty(transaction.balanceAfter)
                                    ? <span className="plr--balance-empty">—</span>
                                    : <div className="plr--balance-line">
                                        <span>{isEmpty(transaction.balanceBefore) ? "—" : utils.convertor(transaction.balanceBefore, currency, 1)}</span>
                                        <span className="plr--balance-arrow">→</span>
                                        <span className="plr--balance-after">{isEmpty(transaction.balanceAfter) ? "—" : utils.convertor(transaction.balanceAfter, currency, 1)}</span>
                                    </div>
                                }
                            </div>

                            {/* Date */}
                            <div className="plr--tx-cell plr--tx-date">
                                <div className="plr--time">{formatTime(transaction.createdAt)}</div>
                                <div className="plr--ago">{formatDay(transaction.createdAt)}</div>
                            </div>

                        </div>
                    )
                })}

            </div>
        )
    }

    /* Draw Content */
    _content = () => {

        /* Fields */
        const { status, data, count, page } = this.state

        /* Check Status */
        if (status !== "ok") {
            return <InnerLayout inner status={status} reload={() => this.load(true)} />
        }

        /* Empty */
        if (!data || data.length === 0) {
            return (
                <div className="plr--empty inner">
                    <RoundIcon size={34} />
                    <span>{t("player.roundsEmpty")}</span>
                </div>
            )
        }

        return (
            <>

                {data.map((item, index) =>
                    <div className={`plr--round ${this.checkRound(item.round) ? "opened" : ""}`} key={`${index}`}>
                        {this._head(item)}
                        {this._transactions(item)}
                    </div>
                )}

                <Pagination size={SIZE} page={page} total={count} onPageChange={(page) => this.changePage(page)} />

            </>
        )
    }

    render = () => {

        return (
            <div className="plr--rounds">

                {/* Draw The Toolbar */}
                {this._toolbar()}

                {/* Draw The Chips Of The Filter */}
                {this._chips()}

                {/* Draw The Numbers */}
                {this._numbers()}

                {/* Draw Content */}
                {this._content()}

                {/* Draw The Filter (только период и игры — раунды принадлежат одному игроку) */}
                <Filter
                    apply={this.applyFilter}
                    hide={["sort", "players", "operators"]}
                    defaultPeriod="all"
                    ref={(ref) => this._filterModal = ref}
                />

            </div>
        )
    }

}

const mapStateToProps = (state) => ({
    language: state.language,
})

export default connect(mapStateToProps)(Rounds)
